/**
 * Counters (reference-data tables) API hooks. Backed by /api/v1/counters in
 * datahub-api. Hand-shaped types mirror the Go models so the UI doesn't have
 * to chase generated names through the SDK; regenerate via `npm run api` if
 * the wire shape diverges.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { client } from '../generated/client.gen';

export interface CustomField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'reference';
  i18n: boolean;
  required: boolean;
  targetTable?: string;
}

export interface CounterRow {
  key: string;
  value: Record<string, string>;
  description?: Record<string, string>;
  parent?: string;
  custom?: Record<string, unknown>;
  draft?: Record<string, unknown>;
}

export interface CounterTable {
  id: string;
  name: string;
  key: string;
  description?: string;
  defaultLang: string;
  langs: string[];
  customFields?: CustomField[];
  rows: CounterRow[];
  usedIn: number;
  updatedAt: string;
  updatedBy?: string;
  pendingReview?: string;
}

export interface CounterTableSummary {
  id: string;
  name: string;
  key: string;
  description?: string;
  defaultLang: string;
  langs: string[];
  rowCount: number;
  fieldCount: number;
  usedIn: number;
  updatedAt: string;
  updatedBy?: string;
  pendingReview?: string;
}

const tablesKey = ['counters', 'list'] as const;
const tableKey = (key: string) => ['counters', 'detail', key] as const;

// Keys may be dot-paths ("contact.profile.gender") — always percent-encode them in URL paths.
const enc = encodeURIComponent;

function unwrap<T>(data: { data?: T } | T): T {
  if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as { data: unknown }).data)) {
    return (data as { data: T }).data;
  }
  return data as T;
}

function useListTables(query?: string) {
  return useQuery({
    queryKey: [...tablesKey, query ?? ''] as const,
    queryFn: async (): Promise<CounterTableSummary[]> => {
      try {
        const { data } = await client.instance.get<{ data: CounterTableSummary[] }>('/counters', {
          params: query ? { q: query } : undefined,
        });
        // Coerce to an array so a malformed/error response body can't crash the
        // grid with "n.map is not a function" (e.g. a 200 HTML fallback).
        const result = unwrap(data);
        return Array.isArray(result) ? result : [];
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return [];
        throw e;
      }
    },
    staleTime: 30 * 1000,
  });
}

function useTable(key: string, opts?: { lang?: string }) {
  return useQuery({
    queryKey: [...tableKey(key), opts?.lang ?? ''] as const,
    queryFn: async (): Promise<CounterTable> => {
      const { data } = await client.instance.get<CounterTable>(`/counters/${enc(key)}`, {
        params: opts?.lang ? { lang: opts.lang } : undefined,
      });
      return data;
    },
    enabled: !!key,
  });
}

export interface CreateCounterTableBody {
  name: string;
  key: string;
  description?: string;
  defaultLang: string;
  langs: string[];
  customFields?: CustomField[];
}

function useCreateTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCounterTableBody): Promise<CounterTable> => {
      const { data } = await client.instance.post<CounterTable>('/counters', body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tablesKey }),
  });
}

function useUpdateSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      customFields,
      confirm,
    }: {
      key: string;
      customFields: CustomField[];
      confirm?: boolean;
    }): Promise<CounterTable> => {
      const { data } = await client.instance.patch<CounterTable>(
        `/counters/${enc(key)}/schema`,
        { customFields },
        { params: confirm ? { confirm: 'drop-data' } : undefined },
      );
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

function useUpdateMeta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      name,
      description,
      newKey,
    }: {
      key: string;
      name?: string;
      description?: string;
      newKey?: string;
    }): Promise<CounterTable> => {
      const { data } = await client.instance.patch<CounterTable>(`/counters/${enc(key)}`, {
        name,
        description,
        key: newKey,
      });
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: tablesKey });
      qc.invalidateQueries({ queryKey: tableKey(vars.key) });
      if (vars.newKey) qc.invalidateQueries({ queryKey: tableKey(vars.newKey) });
    },
  });
}

function useDeleteTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      await client.instance.delete(`/counters/${enc(key)}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tablesKey }),
  });
}

function useAddRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, row }: { key: string; row: CounterRow }) => {
      const { data } = await client.instance.post<CounterTable>(`/counters/${enc(key)}/rows`, row);
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

function useUpdateRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, rowKey, row }: { key: string; rowKey: string; row: CounterRow }) => {
      const { data } = await client.instance.patch<CounterTable>(
        `/counters/${enc(key)}/rows/${enc(rowKey)}`,
        row,
      );
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

function useDeleteRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, rowKey }: { key: string; rowKey: string }) => {
      await client.instance.delete(`/counters/${enc(key)}/rows/${enc(rowKey)}`);
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

function useAddLanguage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      lang,
      autoTranslate,
    }: {
      key: string;
      lang: string;
      autoTranslate: boolean;
    }) => {
      const { data } = await client.instance.post<{ table: CounterTable; jobId?: string }>(
        `/counters/${enc(key)}/languages`,
        { lang, autoTranslate },
      );
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

function useRemoveLanguage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, lang }: { key: string; lang: string }) => {
      const { data } = await client.instance.delete<CounterTable>(
        `/counters/${enc(key)}/languages/${enc(lang)}`,
      );
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

function useAcceptTranslations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, lang }: { key: string; lang: string }) => {
      const { data } = await client.instance.post<CounterTable>(
        `/counters/${enc(key)}/translations/accept`,
        { lang },
      );
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

function useImportCSV() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      file,
      dryRun,
    }: {
      key: string;
      file: File;
      dryRun?: boolean;
    }) => {
      const form = new FormData();
      form.append('file', file);
      const { data } = await client.instance.post<{ imported: number; dryRun?: boolean }>(
        `/counters/${enc(key)}/import`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: dryRun ? { dryRun: '1' } : undefined,
        },
      );
      return data;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: tableKey(vars.key) }),
  });
}

async function exportCSV(key: string, lang?: string) {
  const { data } = await client.instance.get<Blob>(`/counters/${enc(key)}/export.csv`, {
    params: lang ? { lang } : undefined,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${key.replace(/[:.]/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const countersApi = {
  useListTables,
  useTable,
  useCreateTable,
  useUpdateSchema,
  useUpdateMeta,
  useDeleteTable,
  useAddRow,
  useUpdateRow,
  useDeleteRow,
  useAddLanguage,
  useRemoveLanguage,
  useAcceptTranslations,
  useImportCSV,
  exportCSV,
};
